import {
	BASE_PRECISION_EXP,
	BigNum,
	PerpMarketConfig,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	OpenPosition,
	UIMarket,
	calculatePnlPctFromPosition,
} from '@velocity-exchange/common';
import { getMarginUsedForPosition } from 'src/utils/position';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { AccountOpenPosition } from 'src/@types/positions';
import { OpenPositionsMobileCard } from './OpenPositionsMobileCard';
import { BalanceDisplayType } from 'src/@types/types';
import { ExpandableItemParams } from 'src/hooks/lists/expandable/useExpandableItem';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { formatText, PRESETS } from '@velocity-exchange/common/format';

interface OpenPositionTableRowProps extends ExpandableItemParams {
	position: AccountOpenPosition;
	handleSelection: (market: PerpMarketConfig) => void;
	handleShowPnlModal: (
		position: OpenPosition,
		pnl: number,
		isIsolated?: boolean
	) => void;
	sizeDisplayType: BalanceDisplayType;
	onToggleSizeDisplayType: () => void;
}

export const OpenPositionTableRow = ({
	position,
	handleSelection,
	handleShowPnlModal,
	areAllItemsExpanded,
	handleItemExpandedChange,
	sizeDisplayType,
	onToggleSizeDisplayType,
}: OpenPositionTableRowProps) => {
	const { marketSymbol, marketIndex, direction, baseSize } = position;

	const baseSizeBigNum = BigNum.from(baseSize.abs(), BASE_PRECISION_EXP);

	const uiMarket = UIMarket.createPerpMarket(marketIndex);

	const showAccountValues = useShowAccountValues();
	const [pnlReference] = useSyncLocalStorage<'mark' | 'oracle'>(
		'pnlReference',
		'mark'
	);

	const positionPnl = BigNum.from(
		pnlReference === 'mark' ? position.pnlVsMark : position.pnlVsOracle,
		QUOTE_PRECISION_EXP
	);

	const netPnl = positionPnl; // we will remove funding pnl from the UI position table pnl for now, since the UI's wording needs to improve to better inform users that pnl includes funding pnl
	const pnl = netPnl.toNum();
	const notionalPnl = showAccountValues
		? formatText(netPnl, PRESETS.usd)
		: '∗∗∗∗∗∗';

	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const user = velocityClient?.hasUser()
		? velocityClient?.getUser()
		: undefined;

	const percentageNum = calculatePnlPctFromPosition(
		netPnl.val,
		position,
		user
			? getMarginUsedForPosition(user, position.marketIndex, false)
			: undefined
	);

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const handleShowPnlCard = () => {
		handleShowPnlModal(position, pnl, position.isIsolated);
	};

	return (
		<OpenPositionsMobileCard
			uiMarket={uiMarket}
			marketSymbol={uiMarket.uiSymbols.marketDisplaySymbol ?? marketSymbol}
			position={position}
			positionDirection={direction}
			showAccountValues={showAccountValues}
			displayValues={{
				baseSize: baseSizeBigNum,
				positionPnl: positionPnl,
				notionalPnl: notionalPnl,
				percentagePnl: percentagePnl,
			}}
			handleShowPnlCard={handleShowPnlCard}
			areAllItemsExpanded={areAllItemsExpanded}
			handleItemExpandedChange={handleItemExpandedChange}
			handleSelection={handleSelection}
			sizeDisplayType={sizeDisplayType}
			onToggleSizeDisplayType={onToggleSizeDisplayType}
		/>
	);
};
